import { useState } from "react";
import { useSession } from "next-auth/client";
import { AddIcon } from "@chakra-ui/icons";
import {
  VStack,
  StackDivider,
  Box,
  Button,
  Text,
  Center,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
} from "@chakra-ui/react";
import PuffLoader from "react-spinners/PuffLoader";
import toast from "react-hot-toast";
import { TweetItem } from "./tweet-item";
import { isNumeric } from "utils/helpers";
import { getToken } from "firebaseClient";
import { PlusIcon } from "components/icons/PlusIcon";

interface Props {
  editMode?: boolean;
  tweets: any[];
  isLoading?: boolean;
  onDelete?: (id: string) => void;
  onAdd?: () => void;
}

export function Threads({
  editMode = false,
  tweets = [],
  isLoading = false,
  onDelete,
  onAdd
}: Props) {
  const [thread, setThread] = useState<string>("");
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [session] = useSession();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleAdd = async () => {
    try {
      if (!session) {
        toast.error("User is not authenticated");
        return;
      }
      setIsAdding(true);

      const tweetIds = thread.split(",");
      const formattedIds: string[] = [];

      tweetIds.forEach((t) => {
        t = t.split("?")[0];
        if (isNumeric(t)) {
          formattedIds.push(t);
        } else if (t.includes("twitter.com") || t.includes("x.com")) {
          let split = t.split("/status/");
          if (isNumeric(split[split?.length - 1])) {
            //@ts-ignore
            formattedIds.push(split[split?.length - 1]);
          }
        } else {
          console.log("Not suported");
          toast.error("Can't read the tweets, check your formating");
          onClose();
        }
      });

      const dataToSend = {
        tweetIds: formattedIds,
      };
      const token = await getToken(session, "importThreadsById");
      let url = "https://us-central1-ez4cast.cloudfunctions.net/tweetThreadsHunter-importThreadsById"
      let res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          tokenuserid: session?.user?.uid ?? "",
        },
        body: JSON.stringify(dataToSend),
      });
      let data = await res.json();

      if (data.success) {
        toast.success("Successfully added threads");
        setThread("");
        onClose();
        onAdd && onAdd();
      } else {
        toast.error(
          "Invalid tweet links/Ids or you've entered tweets older than 7-8 days from are now"
        );
      }
    } catch (err) {
      console.log("Error in adding thread: ", err);
      toast.error("Error in adding thread: " + err.message);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      <VStack
        divider={<StackDivider borderColor="gray.200" />}
        spacing="6"
        align="stretch"
        maxW="500px"
      >
        {editMode && (
          <Box mx="auto">
            <Button
              variant="tertiaryNoBackground"
              colorScheme="teal"
              onClick={onOpen}
              leftIcon={PlusIcon} >
              Add a new thread
            </Button>
          </Box>
        )}
        {editMode && !isLoading && tweets?.length === 0 && (
          <VStack mt="20">
            <Text textStyle="body.medium.standard">No thread found</Text>
            <Button variant={"primary"} onClick={onOpen}
              leftIcon={PlusIcon}
            >
              Add a thread
            </Button>
          </VStack>
        )}
        {isLoading && (
          <Center pt={10}>
            <Box>
              <PuffLoader
                color={"hsl(203, 100%, 36%)"}
                loading={isLoading}
                css="display: block; margin: 0 auto; border-color: red;"
                size={50}
              />
            </Box>
          </Center>
        )}
        {!isLoading &&
          tweets.map((tw) => (
            <TweetItem
              tweet={tw}
              key={tw.id}
              editMode={editMode}
              onDelete={onDelete}
            />
          ))}
      </VStack>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setThread("");
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add a thread</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Paste first tweet URL or ID</Text>
            <Input
              borderRadius="lg"
              mt="4"
              placeholder="1900139830782149034,https://x.com/patxol/status/1900139830782149034"
              value={thread}
              onChange={(e) => setThread(e.target.value)}
            />
            <Text fontSize="xs" fontStyle="italic" mt="2">Threads from only last 7 days are valid</Text>
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose} mr={3}>Cancel</Button>
            <Button
              variant={"primary"}
              mr={3}
              onClick={handleAdd}
              isLoading={isAdding}
            >
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
